import { ApolloClient, gql } from "@apollo/client";
import {
  formatZodErrorForLogging,
  getProductionEnvironment,
} from "@hex/common";
import { useEffect } from "react";
import { z } from "zod";

import { webClientVersion } from "../util/data";
import { logInfoMsg } from "../util/logging.js";
import { useHexFlag } from "../util/useHexFlags.js";

import {
  ProductVersionsDocument,
  useProductVersionsQuery,
} from "./useProductVersionStatus.generated";

gql`
  query ProductVersions {
    productVersions {
      id
      appVersion
      clientVersion
      kernelVersion
      sidecarVersion
    }
  }
`;

interface ProductVersionStatus {
  appStale: boolean;
  kernelStale: boolean;
  sidecarStale: boolean;
  clientVersion: string | null;
  latestClientVersion: string | null;
  serverVersion: string | null;
}

/**
 * Every 5 minutes, request the latest product version from the server.
 *
 * If the latest possible client version has changed and the client's version is determined to be sufficiently stale
 * based on critiera configured in LaunchDarkly, the client will be automatically reloaded.
 * Refresh criteria are controlled via LaunchDarkly here:
 * https://app.launchdarkly.com/projects/default/flags/auto-refresh-client-config
 */
export const useProjectVersionCheck = (client: ApolloClient<object>): void => {
  const autoRefreshConfig = useHexFlag("auto-refresh-client-config");

  const { clientVersion, latestClientVersion } = useProductVersionStatus({});

  // Every 5 minutes, check if the app version has changed
  const interval = 60 * 5 * 1000;
  useEffect(() => {
    const id = setInterval(() => refreshProductVersionStatus(client), interval);
    return () => clearInterval(id);
  }, [client, interval]);

  const currentEnvironment = getProductionEnvironment(window.location.hostname);
  // If the app version has changed and the version is determined to be stale, reload the client.
  useEffect(() => {
    if (
      currentEnvironment !== "local" &&
      autoRefreshConfig != null &&
      autoRefreshConfig.enabled &&
      clientVersion != null &&
      latestClientVersion != null
    ) {
      try {
        const shouldRefresh = evaluateRefreshCriteria({
          clientVersion,
          latestClientVersion,
          rawFlagConfig: autoRefreshConfig,
        });

        if (shouldRefresh) {
          logInfoMsg(
            "auto-refresh-client-config: reloading client due to stale version",
            {
              safe: {
                clientVersion,
                latestClientVersion,
              },
            },
          );
          window.location.reload();
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [
    autoRefreshConfig,
    clientVersion,
    currentEnvironment,
    latestClientVersion,
  ]);
};

/** Retrieve cached product versions */
export const useProductVersionStatus = ({
  currentKernelVersion,
  currentSidecarVersion,
}: {
  currentKernelVersion?: string;
  currentSidecarVersion?: string;
}): ProductVersionStatus => {
  const { data, error, loading } = useProductVersionsQuery();

  if (!data || error || loading) {
    return {
      appStale: false,
      kernelStale: false,
      sidecarStale: false,
      clientVersion: null,
      latestClientVersion: null,
      serverVersion: null,
    };
  }

  return {
    clientVersion: webClientVersion ?? null,
    latestClientVersion: data.productVersions.clientVersion,
    serverVersion: data.productVersions.appVersion,
    appStale:
      webClientVersion != null &&
      webClientVersion !== data?.productVersions.appVersion,
    kernelStale:
      currentKernelVersion != null &&
      currentKernelVersion !== data?.productVersions.kernelVersion,
    sidecarStale:
      currentSidecarVersion != null &&
      currentSidecarVersion !== data?.productVersions.sidecarVersion,
  };
};

/** Used to trigger a refresh of the latest app versions from the backend */
export const refreshProductVersionStatus = (
  client: ApolloClient<unknown>,
): void => {
  void client.query({
    query: ProductVersionsDocument,
    fetchPolicy: "network-only",
  });
};

const AutoClientRefreshFlagConfig = z.object({
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- building a schema is pure; the rule cannot see through a chained builder
  badClientVersion: z.union([z.string(), z.null()]).optional(),
  enabled: z.boolean(),
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- building a schema is pure; the rule cannot see through a chained builder
  majorVersionDiff: z.union([z.number(), z.null()]).optional(),
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- building a schema is pure; the rule cannot see through a chained builder
  minorVersionDiff: z.union([z.number(), z.null()]).optional(),
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- building a schema is pure; the rule cannot see through a chained builder
  patchVersionDiff: z.union([z.number(), z.null()]).optional(),
});
type AutoClientRefreshFlagConfig = z.infer<typeof AutoClientRefreshFlagConfig>;

/**
 * Utility function to evaluate whether the client should refresh based on the
 * client's version, the latest possible client version, and the config object
 * received from LaunchDarkly.
 *
 * Exported for testing purposes.
 */
export const evaluateRefreshCriteria = ({
  clientVersion,
  latestClientVersion,
  rawFlagConfig,
}: {
  clientVersion: string;
  latestClientVersion: string;
  rawFlagConfig: Record<string, unknown>;
}): boolean => {
  // Return early if client and latest versions match
  if (clientVersion === latestClientVersion) {
    return false;
  }

  const configResult = AutoClientRefreshFlagConfig.safeParse(rawFlagConfig);

  // Return early if the configuration is invalid, or disabled
  if (!configResult.success || configResult.data.enabled !== true) {
    if (!configResult.success) {
      console.error(
        `AutoClientRefreshFlagConfig validation failed: ${formatZodErrorForLogging(configResult.error)}`,
      );
    }
    return false;
  }

  const {
    badClientVersion,
    majorVersionDiff,
    minorVersionDiff,
    patchVersionDiff,
  } = configResult.data;

  // Return early if nothing to evaluate
  if (
    badClientVersion == null &&
    majorVersionDiff == null &&
    minorVersionDiff == null &&
    patchVersionDiff == null
  ) {
    return false;
  }

  // Split the version strings into their major, minor, and patch components
  // Assume a version string is in the format "major.minor.patch", ignoring anything
  // after the third period for hotfix versions or non-production versions with commit hashes.
  // ie 3.0.163-rc.1 or 3.0.163-rc.1-29-ga106ffe2f07639f would both be treated as 3.0.163.
  // parseInt(163-rc) will return 163, which is what we want.
  const [clientMajor, clientMinor, clientPatch] = clientVersion.split(".");
  const [latestMajor, latestMinor, latestPatch] =
    latestClientVersion.split(".");

  const clientMajorVersionInt = parseInt(clientMajor);
  const latestMajorVersionInt = parseInt(latestMajor);

  // If the client's major version is flagged as old, force a refresh.
  if (
    majorVersionDiff != null &&
    latestMajorVersionInt - clientMajorVersionInt >= majorVersionDiff
  ) {
    return true;
  }

  // If the client's minor version is flagged as old and the
  // client's major version matches the latest's major version,
  // force a refresh.
  const clientMinorVersionInt = parseInt(clientMinor);
  const latestMinorVersionInt = parseInt(latestMinor);
  if (
    minorVersionDiff != null &&
    latestMajorVersionInt === clientMajorVersionInt &&
    latestMinorVersionInt - clientMinorVersionInt >= minorVersionDiff
  ) {
    return true;
  }

  // If the client's patch version is flagged as old and the
  // client's major and minor versions match the latest's, force a refresh.
  const clientPatchVersionInt = parseInt(clientPatch);
  const latestPatchVersionInt = parseInt(latestPatch);
  if (
    patchVersionDiff != null &&
    latestMajorVersionInt === clientMajorVersionInt &&
    latestMinorVersionInt === clientMinorVersionInt &&
    latestPatchVersionInt - clientPatchVersionInt >= patchVersionDiff
  ) {
    return true;
  }

  // If the client's specific version has been specifically flagged as bad,
  // force a refresh regardless of whether it was considered "old"
  if (badClientVersion != null && clientVersion === badClientVersion) {
    return true;
  }

  return false;
};
